import { createRouter, createWebHistory } from 'vue-router'
import { nextTick } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useProjectsStore } from '@/stores/projects'
import { useRecordingGuard } from '@/composables/useRecordingGuard'

declare module 'vue-router' {
  interface RouteMeta {
    guestOnly?: boolean
    requiresAuth?: boolean
  }
}

const ProjectsView = () => import('@/views/ProjectsView.vue')
const LoginView = () => import('@/views/LoginView.vue')
const RecordView = () => import('@/views/RecordView.vue')
const PlanReviewView = () => import('@/views/PlanReviewView.vue')
const HistoryView = () => import('@/views/HistoryView.vue')
const SettingsView = () => import('@/views/SettingsView.vue')

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      name: 'projects',
      component: ProjectsView,
      meta: { requiresAuth: true },
    },
    {
      path: '/record',
      name: 'record',
      component: RecordView,
      meta: { requiresAuth: true },
    },
    {
      path: '/login',
      name: 'login',
      component: LoginView,
      meta: { guestOnly: true },
    },
    {
      path: '/history',
      name: 'history',
      component: HistoryView,
      meta: { requiresAuth: true },
    },
    {
      path: '/settings',
      name: 'settings',
      component: SettingsView,
      meta: { requiresAuth: true },
    },
    {
      path: '/projects/:projectId/voice-plans/:voicePlanId',
      name: 'plan-review',
      component: PlanReviewView,
      meta: { requiresAuth: true },
    },
  ],
})

router.beforeEach(async (to, from) => {
  // Catches every way of leaving the Record screen mid-recording — the
  // in-app back button already confirms via this same flow, but a native
  // back gesture, hardware back button, or browser back button goes through
  // popstate and would otherwise unmount RecordView directly, discarding the
  // recording with no confirmation at all.
  if (from.name === 'record' && to.name !== 'record') {
    const allowed = await useRecordingGuard().confirmLeave()
    if (!allowed) return false
  }

  const auth = useAuthStore()
  const projects = useProjectsStore()

  // Resolve the session once on first navigation (app-load probe). fetchUser()
  // rethrows on non-401 errors (e.g. a network blip or 500) — catch here so a
  // transient failure doesn't surface as an uncaught rejection and stall
  // navigation; auth.initialized is still set (in fetchUser()'s finally), so
  // the guard below falls through treating the session as unauthenticated.
  if (!auth.initialized) {
    try {
      await auth.fetchUser()
    } catch (e: unknown) {
      console.error('Failed to resolve the current session:', e)
    }
  }

  const isAuth = auth.isAuthenticated

  if (to.meta.guestOnly) {
    return isAuth ? { name: 'projects' } : true
  }

  if (to.meta.requiresAuth && !isAuth) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }

  // Load once on app boot so a direct nav (e.g. a reload on /history) has
  // the project list available to resolve the persisted activeProjectId,
  // instead of bouncing to the picker just because ProjectsView never mounted.
  if (to.meta.requiresAuth && !projects.initialized) {
    await projects.fetchProjects()
  }

  return true
})

export function focusPageHeading(): void {
  document.querySelector<HTMLElement>('[data-page-heading]')?.focus({ preventScroll: true })
}

// Screen reader users land back on the new page with focus wherever it was
// left (often a now-removed element) and no cue anything changed. Move focus
// to the destination's heading so it's announced, once the DOM has updated.
router.afterEach(() => {
  void nextTick(focusPageHeading)
})

export default router
